/**
 * Plain browser: the PWA.
 */
import type { ApiClient } from '@/api/client'
import type { Tokens } from '@/api/types'
import {
  LocationDenied,
  LocationUnavailable,
  type Coordinates,
  type LocationPermission,
  type Platform,
} from './types'

const GEO_TIMEOUT_MS = 15000

export function createBrowserPlatform(): Platform {
  return {
    kind: 'browser',

    async init() {
      const root = document.documentElement
      root.dataset.platform = 'browser'

      // The brand layer switches on data-theme; Telegram sets it from its own
      // colorScheme, here it follows the system.
      const dark = window.matchMedia?.('(prefers-color-scheme: dark)')
      const apply = () => {
        root.dataset.theme = dark?.matches ? 'dark' : 'light'
      }
      apply()
      dark?.addEventListener?.('change', apply)
    },

    async authenticate(api: ApiClient): Promise<Tokens | null> {
      // Whatever is in localStorage; otherwise the Login screen asks for an email
      return api.tokens
    },

    getLocation(): Promise<Coordinates> {
      return new Promise((resolve, reject) => {
        if (!navigator.geolocation) {
          reject(new LocationUnavailable())
          return
        }
        navigator.geolocation.getCurrentPosition(
          (position) => resolve({ lat: position.coords.latitude, lon: position.coords.longitude }),
          (error) => reject(error.code === error.PERMISSION_DENIED ? new LocationDenied() : new LocationUnavailable()),
          { enableHighAccuracy: true, timeout: GEO_TIMEOUT_MS, maximumAge: 60000 },
        )
      })
    },

    // Safari не знает имени 'geolocation' и бросает, старые браузеры не знают
    // самого Permissions API: и там и там честный ответ — «неизвестно»
    async readLocationPermission(): Promise<LocationPermission> {
      if (!navigator.permissions?.query) return 'unknown'
      try {
        const status = await navigator.permissions.query({ name: 'geolocation' as PermissionName })
        return status.state as LocationPermission
      } catch {
        return 'unknown'
      }
    },

    // A browser has no way to revoke geolocation from code — only the user can,
    // through the site settings behind the padlock
    openLocationSettings() {
      return false
    },

    openExternal(url: string) {
      window.location.href = url
    },

    // Stars are paid inside Telegram only
    async openInvoice() {
      return 'failed' as const
    },

    // The browser already has a back gesture and a back button of its own
    backButton: {
      show() {
        document.documentElement.dataset.back = 'on'
      },
      hide() {
        delete document.documentElement.dataset.back
      },
    },

    theme() {
      return window.matchMedia?.('(prefers-color-scheme: dark)').matches ? 'dark' : 'light'
    },

    haptic() {
      // Nothing to do outside Telegram
    },
  }
}
